import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './CelebrationModal.css';

interface CelebrationData {
  earned_reward: boolean;
  reward_code: string;
  reward_description: string;
  expires_at: string;
  member_name: string;
  store_name: string | null;
}

interface CelebrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  celebration: CelebrationData;
}

// Simple confetti particle
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  size: number;
  shape: 'square' | 'circle' | 'triangle';
}

const CelebrationModal: React.FC<CelebrationModalProps> = ({
  isOpen,
  onClose,
  celebration,
}) => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showBalloons, setShowBalloons] = useState(false);

  // Format expiration date
  const formatExpirationDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Confetti animation
  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Confetti colors (Red Bridge Feed palette + celebration colors)
    const colors = [
      '#DC2626', // Red
      '#059669', // Emerald
      '#FBBF24', // Gold
      '#3B82F6', // Blue
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#F97316', // Orange
    ];

    // Create particles
    const particles: Particle[] = [];
    const particleCount = 150;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 100,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        size: Math.random() * 8 + 4,
        shape: ['square', 'circle', 'triangle'][Math.floor(Math.random() * 3)] as any,
      });
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate((particle.rotation * Math.PI) / 180);
        ctx.fillStyle = particle.color;

        if (particle.shape === 'square') {
          ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        } else if (particle.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.moveTo(0, -particle.size / 2);
          ctx.lineTo(particle.size / 2, particle.size / 2);
          ctx.lineTo(-particle.size / 2, particle.size / 2);
          ctx.closePath();
          ctx.fill();
        }

        ctx.restore();

        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.05; // gravity
        particle.rotation += particle.rotationSpeed;

        // Reset particles that fall off screen
        if (particle.y > canvas.height + 20) {
          particle.y = -20;
          particle.x = Math.random() * canvas.width;
          particle.vy = Math.random() * 3 + 2;
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Show balloons after a short delay
    setTimeout(() => setShowBalloons(true), 300);

    // Play celebration sound (optional)
    if (soundEnabled) {
      try {
        // Create a simple celebration sound using Web Audio API
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const playNote = (frequency: number, delay: number, duration: number) => {
          const oscillator = audioCtx.createOscillator();
          const gainNode = audioCtx.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioCtx.destination);
          oscillator.frequency.value = frequency;
          oscillator.type = 'sine';
          gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime + delay);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + delay + duration);
          oscillator.start(audioCtx.currentTime + delay);
          oscillator.stop(audioCtx.currentTime + delay + duration);
        };
        // Play a simple celebration melody
        playNote(523.25, 0, 0.15); // C
        playNote(659.25, 0.1, 0.15); // E
        playNote(783.99, 0.2, 0.15); // G
        playNote(1046.5, 0.3, 0.3); // High C
      } catch (e) {
        // Audio not supported, that's okay
      }
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isOpen, soundEnabled]);

  const handleViewReward = () => {
    onClose();
    navigate('/bridge-club/card');
  };

  if (!isOpen) return null;

  return (
    <div className="celebration-overlay">
      {/* Confetti Canvas */}
      <canvas ref={canvasRef} className="celebration-canvas" />

      {/* Floating Balloons */}
      {showBalloons && (
        <div className="celebration-balloons">
          <div className="balloon balloon-red" style={{ left: '10%', animationDelay: '0s' }} />
          <div className="balloon balloon-gold" style={{ left: '25%', animationDelay: '0.2s' }} />
          <div className="balloon balloon-emerald" style={{ left: '40%', animationDelay: '0.1s' }} />
          <div className="balloon balloon-blue" style={{ left: '55%', animationDelay: '0.3s' }} />
          <div className="balloon balloon-purple" style={{ left: '70%', animationDelay: '0.15s' }} />
          <div className="balloon balloon-pink" style={{ left: '85%', animationDelay: '0.25s' }} />
        </div>
      )}

      {/* Modal Content */}
      <div className="celebration-modal">
        {/* Sound Toggle */}
        <button
          className="celebration-sound-toggle"
          onClick={() => setSoundEnabled(!soundEnabled)}
          aria-label={soundEnabled ? 'Mute sound' : 'Enable sound'}
        >
          {soundEnabled ? '🔊' : '🔇'}
        </button>

        {/* Close Button */}
        <button className="celebration-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* Celebration Title */}
        <div className="celebration-title">
          <span className="celebration-emoji">🎉</span>
          <span className="celebration-text">CONGRATULATIONS!</span>
          <span className="celebration-emoji">🎉</span>
        </div>

        {/* Subtitle */}
        <div className="celebration-subtitle">You did it!</div>

        {/* Thank You Message */}
        <p className="celebration-thanks">
          Thank you for supporting our small family business!
        </p>

        {/* Reward Message */}
        <div className="celebration-reward">
          <div className="reward-badge">FREE BAG</div>
          <p className="reward-text">You've earned a FREE BAG of Red Bridge Feed!</p>
        </div>

        {/* Redemption Code */}
        <div className="celebration-code-section">
          <p className="code-label">
            Show this code at {celebration.store_name || 'your local store'} to claim your reward:
          </p>
          <div className="code-display">
            <div className="code-qr">
              <QRCodeSVG
                value={celebration.reward_code}
                size={100}
                level="H"
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </div>
            <div className="code-text">{celebration.reward_code}</div>
          </div>
          <p className="code-expiry">
            Valid until {formatExpirationDate(celebration.expires_at)}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="celebration-actions">
          <button className="btn-primary celebration-btn" onClick={handleViewReward}>
            View My Reward
          </button>
          <button className="btn-secondary celebration-btn" onClick={onClose}>
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default CelebrationModal;
